/**
 * Event Viewer — Journaux Windows. Plugin C# RemoteWS.Plugin.EventViewer.
 * Côté agent : API native System.Diagnostics.Eventing.Reader.
 *
 * v2 : multi-canal merge, fenêtre temporelle, curseur de pagination,
 * filtres avancés (eventIds, providers, excludeProviders, messageContains).
 *
 * Tant que `ws.state.agentFeatures.eventviewer` est faux, affiche un
 * PanelPreview avec le contrat attendu.
 */
import { createEffect, createMemo, createSignal, For, onCleanup, Show, type Component } from "solid-js";
import { PanelLayout } from "@/shared/panel-base";
import { DataTable, type Column } from "@/shared/data-table";
import { createToolQuery } from "@/shared/query";
import { usePanel } from "@/shared/use-panel";
import { useToolClient } from "@/shared/tool-client";
import { useWs } from "@/shared/ws";
import { isEnvelope } from "@/shared/ws-protocol";
import {
  PanelBadge,
  PanelDetail,
  PanelPreview,
  RefreshIndicator,
  type DetailSection,
  type Intent,
} from "@/shared/panel-primitives";
import { usePanelRefresh } from "@/shared/use-panel-refresh";
import { Skeleton } from "@/components/skeleton";
import { registerPanel } from "@/panel-registry";

interface EventRow {
  recordId: number;
  eventId: number;
  level: number;
  levelName: string;
  provider: string;
  channel: string;
  timeMs: number;
  message: string;
}

interface NextCursor {
  timeMs: number;
  recordId: number;
  channel: string;
}

interface ListResult {
  channels: string[];
  limit: number;
  returned: number;
  truncated: boolean;
  scanned: number;
  rows: EventRow[];
  nextCursor?: NextCursor | null;
  cached?: boolean;
  cacheAgeMs?: number;
  incremental?: boolean;
  incrementalAdded?: number;
}

interface ChannelRow {
  name: string;
  isEnabled: boolean;
  logType: string;
  recordCount: number;
}

interface ChannelsResult {
  rows: ChannelRow[];
}

interface EventDetail extends EventRow {
  machineName: string;
  userId?: string | null;
  processId?: number | null;
  threadId?: number | null;
  keywordsDisplay?: string;
  task?: number;
  opcode?: number;
  description: string;
  xml: string;
}

interface GroupRow {
  key: string;
  count: number;
  lastSeenMs: number;
  sample: { eventId: number; provider: string; channel: string; level: number; recordId: number };
}
interface GroupResult {
  groupBy: string;
  topN: number;
  channels: string[];
  total: number;
  scanned: number;
  rows: GroupRow[];
  cached?: boolean;
}

// Le PluginHost JIT sérialise les résultats en PascalCase. On normalise en
// camelCase pour que le panel reste cohérent avec ses interfaces.
function toCamelCase(s: string): string {
  if (!s) return s;
  return s.charAt(0).toLowerCase() + s.slice(1);
}
function normalizeKeys<T>(value: unknown): T {
  if (Array.isArray(value)) return value.map(normalizeKeys) as T;
  if (value && typeof value === "object") {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value)) {
      out[toCamelCase(k)] = normalizeKeys(v);
    }
    return out as T;
  }
  return value as T;
}

interface ChannelNode {
  id: string;
  label: string;
  children: Record<string, ChannelNode>;
  leaf: boolean;
  channel?: ChannelRow;
  parent?: ChannelNode;
  checked: boolean;
  indeterminate: boolean;
  expanded: boolean;
}

function buildChannelTree(rows: ChannelRow[], selected: Set<string>, expanded: Set<string>): ChannelNode {
  const root: ChannelNode = {
    id: "",
    label: "",
    children: {},
    leaf: false,
    checked: false,
    indeterminate: false,
    expanded: true,
  };
  for (const ch of rows) {
    const parts = ch.name.split("/");
    let node = root;
    let path = "";
    for (const part of parts) {
      path = path ? `${path}/${part}` : part;
      if (!node.children[part]) {
        node.children[part] = {
          id: path,
          label: part,
          children: {},
          leaf: false,
          parent: node,
          checked: false,
          indeterminate: false,
          expanded: false,
        };
      }
      node = node.children[part];
    }
    node.leaf = true;
    node.channel = ch;
    node.checked = selected.has(ch.name);
  }
  computeTreeState(root);
  function restore(n: ChannelNode) {
    if (expanded.has(n.id)) n.expanded = true;
    for (const c of Object.values(n.children)) restore(c);
  }
  restore(root);
  return root;
}

function computeTreeState(node: ChannelNode) {
  if (node.leaf) {
    node.indeterminate = false;
    return;
  }
  let allChecked = true;
  let anyChecked = false;
  for (const child of Object.values(node.children)) {
    computeTreeState(child);
    if (child.checked || child.indeterminate) anyChecked = true;
    if (!child.checked) allChecked = false;
  }
  node.checked = allChecked && anyChecked;
  node.indeterminate = anyChecked && !allChecked;
}

function setNodeChecked(node: ChannelNode, checked: boolean, selected: Set<string>) {
  node.checked = checked;
  node.indeterminate = false;
  if (node.leaf) {
    if (checked) selected.add(node.id);
    else selected.delete(node.id);
  }
  for (const child of Object.values(node.children)) setNodeChecked(child, checked, selected);
}

function updateAncestors(node: ChannelNode) {
  let cur = node.parent;
  while (cur) {
    const children = Object.values(cur.children);
    const allChecked = children.every((c) => c.checked && !c.indeterminate);
    const anyChecked = children.some((c) => c.checked || c.indeterminate);
    cur.checked = allChecked && anyChecked;
    cur.indeterminate = anyChecked && !allChecked;
    cur = cur.parent;
  }
}

function collectNodeIds(node: ChannelNode, out: Set<string>) {
  if (node.id) out.add(node.id);
  for (const c of Object.values(node.children)) collectNodeIds(c, out);
}

function nodeMatchesSearch(node: ChannelNode, term: string): boolean {
  if (node.label.toLowerCase().includes(term)) return true;
  return Object.values(node.children).some((c) => nodeMatchesSearch(c, term));
}

function levelIntent(level: number): Intent {
  if (level === 1 || level === 2) return "danger";
  if (level === 3) return "warn";
  if (level === 4) return "info";
  return "muted";
}
function levelLabel(level: number, fallback: string): string {
  if (fallback) return fallback;
  return level === 1
    ? "Critical"
    : level === 2
      ? "Error"
      : level === 3
        ? "Warning"
        : level === 4
          ? "Information"
          : level === 5
            ? "Verbose"
            : `Level ${level}`;
}

function formatRelativeTime(ms: number): string {
  const dt = Date.now() - ms;
  if (dt < 60_000) return `il y a ${Math.round(dt / 1000)}s`;
  if (dt < 3_600_000) return `il y a ${Math.round(dt / 60_000)}min`;
  if (dt < 86_400_000) return `il y a ${Math.round(dt / 3_600_000)}h`;
  const d = new Date(ms);
  return d.toLocaleString();
}

// Presets de fenêtre temporelle — sinceMs = now - delta.
const TIME_PRESETS: Array<{ id: string; label: string; ms: number }> = [
  { id: "1h", label: "1 h", ms: 3_600_000 },
  { id: "6h", label: "6 h", ms: 6 * 3_600_000 },
  { id: "24h", label: "24 h", ms: 24 * 3_600_000 },
  { id: "7d", label: "7 j", ms: 7 * 24 * 3_600_000 },
  { id: "30d", label: "30 j", ms: 30 * 24 * 3_600_000 },
  { id: "all", label: "Tout", ms: 0 },
];

const DEFAULT_CHANNELS = ["Application", "System"];

// Presets d'intervalle pour le mode Live (watch). 0 = Off.
const LIVE_INTERVALS: Array<{ id: string; label: string; ms: number }> = [
  { id: "off", label: "Off", ms: 0 },
  { id: "30s", label: "30 s", ms: 30_000 },
  { id: "60s", label: "1 min", ms: 60_000 },
  { id: "5m", label: "5 min", ms: 300_000 },
];

interface ChannelTreeProps {
  rows: ChannelRow[];
  selected: Set<string>;
  isLoading?: boolean;
  error?: Error | null;
  onChange: (selected: Set<string>) => void;
  onRefresh?: () => void;
}

const ChannelTreeNode: Component<{
  node: ChannelNode;
  search: string;
  onlySelected: boolean;
  expanded: Set<string>;
  onToggle: (node: ChannelNode) => void;
  onExpand: (node: ChannelNode) => void;
  depth: number;
}> = (props) => {
  let cbRef: HTMLInputElement | undefined;
  createEffect(() => {
    if (cbRef) cbRef.indeterminate = props.node.indeterminate;
  });

  const hasChildren = () => Object.keys(props.node.children).length > 0;
  const visibleChildren = createMemo(() => {
    const term = props.search.toLowerCase();
    const all = Object.values(props.node.children).filter((c) => {
      const matchesSearch = !term || nodeMatchesSearch(c, term);
      const matchesSelected = !props.onlySelected || c.checked || c.indeterminate;
      return matchesSearch && matchesSelected;
    });
    all.sort((a, b) => {
      if (!a.leaf && b.leaf) return -1;
      if (a.leaf && !b.leaf) return 1;
      return a.label.localeCompare(b.label);
    });
    return all;
  });

  return (
    <div class="channel-tree-row" style={{ "--depth": String(props.depth) }}>
      <span
        class={`channel-tree-toggle ${hasChildren() ? "" : "channel-tree-toggle--leaf"}`}
        onClick={() => hasChildren() && props.onExpand(props.node)}
      >
        {hasChildren() ? (props.node.expanded ? "▼" : "▶") : ""}
      </span>
      <input
        type="checkbox"
        checked={props.node.checked}
        ref={(el) => {
          cbRef = el;
        }}
        onChange={() => props.onToggle(props.node)}
      />
      <span
        class={`channel-tree-label ${
          props.node.leaf && props.node.channel?.isEnabled === false ? "channel-tree-label--disabled" : ""
        }`}
        onClick={() => props.onToggle(props.node)}
      >
        {props.node.leaf ? (props.node.channel?.name ?? props.node.label) : props.node.label}
      </span>
      <Show when={props.node.leaf && props.node.channel && props.node.channel.recordCount >= 0}>
        <span class="channel-tree-count">{props.node.channel!.recordCount}</span>
      </Show>
      <Show when={hasChildren() && props.node.expanded}>
        <div class="channel-tree-children">
          <For each={visibleChildren()}>
            {(child) => (
              <ChannelTreeNode
                node={child}
                search={props.search}
                onlySelected={props.onlySelected}
                expanded={props.expanded}
                onToggle={props.onToggle}
                onExpand={props.onExpand}
                depth={props.depth + 1}
              />
            )}
          </For>
        </div>
      </Show>
    </div>
  );
};

const ChannelTree: Component<ChannelTreeProps> = (props) => {
  const [search, setSearch] = createSignal("");
  const [onlySelected, setOnlySelected] = createSignal(false);
  const [expanded, setExpanded] = createSignal<Set<string>>(new Set());

  const tree = createMemo(() => buildChannelTree(props.rows, props.selected, expanded()));

  function handleToggle(node: ChannelNode) {
    const next = new Set(props.selected);
    setNodeChecked(node, !node.checked, next);
    updateAncestors(node);
    props.onChange(next);
  }

  function handleExpand(node: ChannelNode) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(node.id)) next.delete(node.id);
      else next.add(node.id);
      return next;
    });
  }

  function expandAll() {
    const next = new Set(expanded());
    collectNodeIds(tree(), next);
    setExpanded(next);
  }

  function collapseAll() {
    setExpanded(new Set<string>());
  }

  const visibleRoots = createMemo(() => {
    const term = search().toLowerCase();
    const all = Object.values(tree().children).filter((c) => {
      const matchesSearch = !term || nodeMatchesSearch(c, term);
      const matchesSelected = !onlySelected() || c.checked || c.indeterminate;
      return matchesSearch && matchesSelected;
    });
    all.sort((a, b) => {
      if (!a.leaf && b.leaf) return -1;
      if (a.leaf && !b.leaf) return 1;
      return a.label.localeCompare(b.label);
    });
    return all;
  });

  return (
    <div class="channel-tree-panel">
      <div class="channel-tree-toolbar">
        <input
          class="panel-filter-input channel-tree-search"
          type="search"
          placeholder="Rechercher un canal..."
          value={search()}
          onInput={(e) => setSearch(e.currentTarget.value)}
        />
        <label class="channel-tree-onlysel">
          <input type="checkbox" checked={onlySelected()} onChange={(e) => setOnlySelected(e.currentTarget.checked)} />
          Uniquement sélectionnés
        </label>
        <button type="button" class="btn btn--xs btn--ghost" onClick={expandAll}>
          Tout déplier
        </button>
        <button type="button" class="btn btn--xs btn--ghost" onClick={collapseAll}>
          Tout replier
        </button>
      </div>
      <div class="channel-tree-body">
        <Show when={!props.isLoading} fallback={<span class="channel-tree-empty">Chargement des canaux…</span>}>
          <Show
            when={props.rows.length > 0}
            fallback={
              <div class="channel-tree-empty">
                <span>Aucun canal disponible.</span>
                <Show when={props.error}>
                  <span class="channel-tree-error">{props.error!.message}</span>
                </Show>
                <Show when={props.onRefresh}>
                  <button type="button" class="btn btn--xs btn--primary" onClick={props.onRefresh}>
                    Recharger
                  </button>
                </Show>
              </div>
            }
          >
            <For each={visibleRoots()}>
              {(child) => (
                <ChannelTreeNode
                  node={child}
                  search={search()}
                  onlySelected={onlySelected()}
                  expanded={expanded()}
                  onToggle={handleToggle}
                  onExpand={handleExpand}
                  depth={0}
                />
              )}
            </For>
          </Show>
        </Show>
      </div>
    </div>
  );
};

const EventViewerPanel: Component = () => {
  const ws = useWs();
  const client = useToolClient();
  const enabled = () => ws.state.agentFeatures?.eventviewer === true;
  const supported = () => ws.state.nativeAgentOnline && ws.state.supportApproved;

  // ── État ──────────────────────────────────────────────────────────────
  const [activeChannels, setActiveChannels] = createSignal<Set<string>>(new Set(DEFAULT_CHANNELS));
  const [levelFilter, setLevelFilter] = createSignal<Set<number>>(new Set([1, 2, 3]));
  const [timePresetId, setTimePresetId] = createSignal<string>("24h");
  const [limit, setLimit] = createSignal(100);
  const [selected, setSelected] = createSignal<EventRow | null>(null);
  // Filtres avancés
  const [eventIdsRaw, setEventIdsRaw] = createSignal("");
  const [providersRaw, setProvidersRaw] = createSignal("");
  const [excludeProvidersRaw, setExcludeProvidersRaw] = createSignal("");
  const [messageContains, setMessageContains] = createSignal("");
  const [advancedOpen, setAdvancedOpen] = createSignal(false);
  // Pagination cumulative — chaque "Charger plus" ajoute des rows et avance le curseur.
  const [accumulatedRows, setAccumulatedRows] = createSignal<EventRow[]>([]);
  const [currentCursor, setCurrentCursor] = createSignal<NextCursor | null>(null);
  // Bump pour relancer la query après "Charger plus" sans changer les filtres.
  const [pageTick, setPageTick] = createSignal(0);
  // Vue : liste plate vs groupée (agrégation côté plugin via events_groupby).
  const [view, setView] = createSignal<"list" | "group">("list");
  const [groupBy, setGroupBy] = createSignal<"eventId" | "provider" | "channel" | "hour" | "day" | "level">("eventId");
  // bypassCache au prochain fetch — bumpé via bouton "Forcer rafraîch."
  const [bypassCache, setBypassCache] = createSignal(false);
  // Live updates : abonnement events_watch_start côté plugin. L'intervalle
  // est sélectionné par l'opérateur, "Off" désactive le push.
  const [liveIntervalId, setLiveIntervalId] = createSignal<string>("off");
  const [liveJobId, setLiveJobId] = createSignal<string | null>(null);
  const [liveLastPushAt, setLiveLastPushAt] = createSignal<number | null>(null);
  // Nombre de rows reçues dans le dernier rattrapage historique (push avec
  // historical:true côté C# = SQL events_cache match). Affiché un instant
  // en tooltip pour signaler "le filtre vient de rattraper N events anciens".
  const [liveLastHistoricalCount, setLiveLastHistoricalCount] = createSignal<number>(0);
  // Events reçus en push WS (prepended côté UI au prochain render).
  const [livePrepended, setLivePrepended] = createSignal<EventRow[]>([]);
  let liveOffWs: (() => void) | null = null;

  // Reset des résultats accumulés quand on touche aux filtres (mais pas au pageTick).
  // Si un abonnement live est actif, on appelle events_watch_update pour
  // changer le filtre côté plugin SANS recréer le Timer ni perdre les
  // checkpoints — le plugin rattrape l'historique correspondant au nouveau
  // filtre instantanément via le SQL events_cache.
  function resetPagination() {
    setAccumulatedRows([]);
    setCurrentCursor(null);
    setLivePrepended([]);
    if (liveJobId() != null) {
      const id = liveIntervalId();
      const preset = LIVE_INTERVALS.find((p) => p.id === id);
      if (preset && preset.ms > 0) {
        queueMicrotask(() => {
          void updateLive(preset.ms);
        });
      }
    }
  }

  // ── Helpers parsing filtres avancés ──────────────────────────────────
  const parseIntList = (s: string): number[] =>
    s
      .split(/[,;\s]+/)
      .map((x) => x.trim())
      .filter(Boolean)
      .map((x) => Number(x))
      .filter((n) => Number.isInteger(n) && n >= 0 && n < 65536);

  const parseStringList = (s: string): string[] =>
    s
      .split(/[,;\n]+/)
      .map((x) => x.trim())
      .filter((x) => x.length > 0 && x.length < 200);

  // ── Channels query (chargée 1×, refetch via bouton ↺) ────────────────
  const channelsQuery = createToolQuery<ChannelsResult>(() => ({
    tool: "events_channels",
    params: { onlyEnabled: true, limit: 200 },
    enabled: supported() && enabled(),
    refetchInterval: 0,
  }));
  const normalizedChannelsData = createMemo(() => normalizeKeys<ChannelsResult | undefined>(channelsQuery.data));

  // Snapshot des paramètres de la query (filtres + curseur + pageTick).
  const queryParams = createMemo(() => {
    const preset = TIME_PRESETS.find((p) => p.id === timePresetId()) || TIME_PRESETS[2];
    const sinceMs = preset.ms === 0 ? 0 : Date.now() - preset.ms;
    pageTick(); // dépendance réactive pour relancer
    return {
      channels: [...activeChannels()],
      levels: [...levelFilter()],
      limit: limit(),
      reverse: true,
      sinceMs,
      eventIds: parseIntList(eventIdsRaw()),
      providers: parseStringList(providersRaw()),
      excludeProviders: parseStringList(excludeProvidersRaw()),
      messageContains: messageContains().trim(),
      cursor: currentCursor() ?? undefined,
      bypassCache: bypassCache(),
    };
  });

  const listQuery = createToolQuery<ListResult>(() => ({
    tool: "events_list",
    params: queryParams(),
    enabled: supported() && enabled() && activeChannels().size > 0 && view() === "list",
    refetchInterval: 0,
  }));

  // ── Vue groupée : agrégation côté plugin via events_groupby ────────────
  const groupByQuery = createToolQuery<GroupResult>(() => {
    const preset = TIME_PRESETS.find((p) => p.id === timePresetId()) || TIME_PRESETS[2];
    const sinceMs = preset.ms === 0 ? 0 : Date.now() - preset.ms;
    pageTick();
    return {
      tool: "events_groupby",
      params: {
        channels: [...activeChannels()],
        levels: [...levelFilter()],
        sinceMs,
        eventIds: parseIntList(eventIdsRaw()),
        providers: parseStringList(providersRaw()),
        excludeProviders: parseStringList(excludeProvidersRaw()),
        groupBy: groupBy(),
        topN: 50,
        bypassCache: bypassCache(),
      },
      enabled: supported() && enabled() && activeChannels().size > 0 && view() === "group",
      refetchInterval: 0,
    };
  });

  const normalizedGroupData = createMemo(() => normalizeKeys<GroupResult | undefined>(groupByQuery.data));

  const normalizedListData = createMemo(() => normalizeKeys<ListResult | undefined>(listQuery.data));

  // Quand une page arrive, on la concatène aux précédentes (cursor != null) ou
  // on remplace (première page). On prepend les events reçus en push live
  // (livePrepended) en tête, dédupliqués par {channel:recordId}.
  const visibleRows = createMemo<EventRow[]>(() => {
    const page = normalizedListData()?.rows ?? [];
    const base = currentCursor() == null ? page : [...accumulatedRows(), ...page];
    const live = livePrepended();
    if (live.length === 0) return base;
    const seen = new Set<string>(base.map((r) => `${r.channel}:${r.recordId}`));
    const fresh = live.filter((r) => !seen.has(`${r.channel}:${r.recordId}`));
    return [...fresh, ...base];
  });

  // ── Live updates : abonnement events_watch_start + listener WS ────────
  // Démarre/arrête un watcher côté plugin selon liveIntervalId(). Le plugin
  // pousse les nouveaux events qui matchent le filtre via live/eventviewer/
  // watch.delta toutes les N secondes (incrémental SQLite-backed).
  async function startLive(intervalMs: number) {
    if (!enabled() || !supported()) return;
    const params = queryParams();
    try {
      const res = await client.call<{ jobId: string; watching: boolean }>(
        "events_watch_start",
        {
          channels: params.channels,
          levels: params.levels,
          eventIds: params.eventIds,
          providers: params.providers,
          excludeProviders: params.excludeProviders,
          messageContains: params.messageContains,
          sinceMs: params.sinceMs,
          intervalMs,
          limit: params.limit,
        },
        { timeoutMs: 10_000 },
      );
      if (!res?.jobId) return;
      setLiveJobId(res.jobId);
      // Listener WS filtré par jobId (envelope live/eventviewer/watch.delta).
      liveOffWs?.();
      liveOffWs = ws.on(
        (m) =>
          isEnvelope(m) &&
          m.family === "live" &&
          m.type === "eventviewer" &&
          m.op === "watch.delta" &&
          (m.payload as { jobId?: string } | undefined)?.jobId === res.jobId,
        (m) => {
          const payload = (
            m as {
              payload?: { rows?: EventRow[]; pushedAt?: number; historical?: boolean };
            }
          ).payload;
          const rows = normalizeKeys<EventRow[]>(payload?.rows ?? []);
          if (rows.length === 0) return;
          setLiveLastPushAt(payload?.pushedAt ?? Date.now());
          if (payload?.historical) setLiveLastHistoricalCount(rows.length);
          // Prepend, dedup interne avec ce qui est déjà dans livePrepended.
          setLivePrepended((prev) => {
            const seen = new Set(prev.map((r) => `${r.channel}:${r.recordId}`));
            const fresh = rows.filter((r) => !seen.has(`${r.channel}:${r.recordId}`));
            // Cap à 500 pour borner la croissance — au-delà on recommande
            // un refresh manuel qui reprend tout depuis le serveur.
            return [...fresh, ...prev].slice(0, 500);
          });
        },
      );
    } catch (err) {
      panel.setActionStatus({
        text: `Live impossible : ${err instanceof Error ? err.message : String(err)}`,
        level: "danger",
      });
    }
  }
  async function stopLive(silent = false) {
    liveOffWs?.();
    liveOffWs = null;
    const jid = liveJobId();
    setLiveJobId(null);
    if (jid) {
      try {
        await client.call("events_watch_stop", { jobId: jid }, { timeoutMs: 5_000 });
      } catch {
        // Best-effort — le watcher sera nettoyé au prochain unload plugin
        // de toute façon.
      }
    }
    if (!silent) setLivePrepended([]);
  }
  function changeLiveInterval(id: string) {
    setLiveIntervalId(id);
    const preset = LIVE_INTERVALS.find((p) => p.id === id);
    if (!preset || preset.ms === 0) {
      // Off → stop watcher s'il existe.
      void stopLive(false);
      return;
    }
    // Watcher déjà actif → events_watch_update (instant, garde checkpoints
    // côté plugin et rattrape l'historique via le SQL events_cache).
    const jid = liveJobId();
    if (jid != null) {
      void updateLive(preset.ms);
    } else {
      void startLive(preset.ms);
    }
  }

  /**
   * Met à jour le filtre d'un watcher existant via events_watch_update.
   * Le plugin SQL-filtre les events déjà indexés (events_cache) et nous
   * envoie immédiatement un push `historical: true` avec le rattrapage.
   * Beaucoup plus rapide que stop+start (pas de SeedCheckpoints, pas de
   * recréation du Timer, pas de gap dans la couverture).
   */
  async function updateLive(intervalMs: number) {
    const jid = liveJobId();
    if (jid == null) return;
    const params = queryParams();
    try {
      await client.call(
        "events_watch_update",
        {
          jobId: jid,
          channels: params.channels,
          levels: params.levels,
          eventIds: params.eventIds,
          providers: params.providers,
          excludeProviders: params.excludeProviders,
          messageContains: params.messageContains,
          sinceMs: params.sinceMs,
          limit: params.limit,
          intervalMs,
        },
        { timeoutMs: 10_000 },
      );
      // Pas besoin de toucher au listener WS : le jobId est le même.
    } catch (err) {
      // Si update échoue (watcher disparu côté plugin), on retombe sur
      // un stop+start propre.
      panel.setActionStatus({
        text: `Update live échoué, redémarrage : ${err instanceof Error ? err.message : String(err)}`,
        level: "warn",
      });
      await stopLive(true);
      await startLive(intervalMs);
    }
  }
  // Nettoyage au démontage du panel (et à toute modif de filtre qui rend
  // l'abonnement obsolète — voir applyAdvanced/toggleChannel etc.).
  onCleanup(() => {
    liveOffWs?.();
    liveOffWs = null;
    const jid = liveJobId();
    if (jid) {
      // Fire-and-forget : on n'attend pas la réponse à l'unmount.
      void client.call("events_watch_stop", { jobId: jid }, { timeoutMs: 5_000 }).catch(() => undefined);
    }
  });

  function loadMore() {
    const cur = normalizedListData()?.nextCursor;
    if (!cur) return;
    // Promu : la page courante devient accumulée, le curseur avance.
    setAccumulatedRows(visibleRows());
    setCurrentCursor(cur);
    setPageTick((n) => n + 1);
  }

  const detailQuery = createToolQuery<EventDetail>(() => ({
    tool: "events_get",
    params: { channel: selected()?.channel ?? "", recordId: selected()?.recordId ?? -1 },
    enabled: supported() && enabled() && selected() != null,
    refetchInterval: 0,
  }));
  const normalizedDetailData = createMemo(() => normalizeKeys<EventDetail | undefined>(detailQuery.data));

  const panel = usePanel(
    () => ({ isFetching: listQuery.isFetching, error: listQuery.error }),
    () => ({ isFetching: detailQuery.isFetching, error: detailQuery.error }),
  );

  const refresh = usePanelRefresh({
    query: () => ({
      isFetching: listQuery.isFetching,
      refetch: () => listQuery.refetch(),
      dataUpdatedAt: listQuery.dataUpdatedAt,
      error: listQuery.error,
    }),
  });

  function toggleLevel(lvl: number) {
    resetPagination();
    setLevelFilter((prev) => {
      const next = new Set(prev);
      if (next.has(lvl)) next.delete(lvl);
      else next.add(lvl);
      return next;
    });
  }
  function changeTimePreset(id: string) {
    resetPagination();
    setTimePresetId(id);
  }
  function changeLimit(n: number) {
    resetPagination();
    setLimit(n);
  }
  function applyAdvanced() {
    resetPagination();
    setPageTick((n) => n + 1);
  }
  function forceRefresh() {
    resetPagination();
    setBypassCache(true);
    setPageTick((n) => n + 1);
    // Reset du flag bypass après le tick — le prochain fetch repassera par cache.
    queueMicrotask(() => setBypassCache(false));
  }
  // Drill-down depuis la vue groupée → bascule en liste filtrée par la clé.
  function drillDown(group: GroupRow) {
    const k = groupBy();
    if (k === "eventId") setEventIdsRaw(group.key);
    else if (k === "provider") setProvidersRaw(group.key);
    else if (k === "channel") {
      // Sélectionne seulement ce canal.
      setActiveChannels(new Set([group.key]));
    } else if (k === "level") {
      const n = parseInt(group.key, 10);
      if (!isNaN(n)) setLevelFilter(new Set([n]));
    }
    // hour/day : on n'a pas de filtre temporel précis → on garde le timePreset
    // courant ; à améliorer avec un sinceMs/untilMs précis si besoin.
    resetPagination();
    setView("list");
    setPageTick((n) => n + 1);
  }

  const columns: Column<EventRow>[] = [
    {
      accessorKey: "level",
      header: "Niveau",
      cell: (info) => {
        const r = info.row.original;
        return <PanelBadge intent={levelIntent(r.level)}>{levelLabel(r.level, r.levelName)}</PanelBadge>;
      },
    },
    {
      accessorKey: "timeMs",
      header: "Date",
      cell: (info) => {
        const ms = Number(info.getValue());
        return <span title={new Date(ms).toLocaleString()}>{formatRelativeTime(ms)}</span>;
      },
    },
    { accessorKey: "channel", header: "Canal" },
    { accessorKey: "eventId", header: "Event ID" },
    { accessorKey: "provider", header: "Source" },
    { accessorKey: "message", header: "Message" },
  ];

  const detailSections = (rec: EventDetail): DetailSection[] => [
    {
      label: "Identité",
      items: [
        ["Canal", <>{rec.channel}</>],
        ["Provider", <>{rec.provider}</>],
        ["Event ID", <>{rec.eventId}</>],
        ["Niveau", <PanelBadge intent={levelIntent(rec.level)}>{levelLabel(rec.level, rec.levelName)}</PanelBadge>],
        ["Record ID", <code>{rec.recordId}</code>],
        ["Date", <span title={new Date(rec.timeMs).toLocaleString()}>{new Date(rec.timeMs).toLocaleString()}</span>],
      ],
    },
    {
      label: "Contexte",
      items: [
        ["Machine", <>{rec.machineName}</>],
        ["User SID", <code>{rec.userId ?? "—"}</code>],
        [
          "Process / Thread",
          <>
            {rec.processId ?? "—"} / {rec.threadId ?? "—"}
          </>,
        ],
        ["Keywords", <>{rec.keywordsDisplay || "—"}</>],
      ],
    },
    {
      label: "Description",
      children: <pre class="event-detail-desc">{rec.description || "(vide)"}</pre>,
    },
    {
      label: "XML brut",
      children: <pre class="event-detail-xml">{rec.xml || "(vide)"}</pre>,
    },
  ];

  // ── Toolbar (ligne 1) : segment list/group + recharger + niveau + temps ──
  const toolbar = (
    <>
      <div class="event-view-switch" role="group" aria-label="Vue">
        <button
          type="button"
          class="btn btn--xs"
          classList={{ "btn--info": view() === "list", "btn--ghost": view() !== "list" }}
          onClick={() => setView("list")}
          aria-pressed={view() === "list"}
        >
          Liste
        </button>
        <button
          type="button"
          class="btn btn--xs"
          classList={{ "btn--info": view() === "group", "btn--ghost": view() !== "group" }}
          onClick={() => setView("group")}
          aria-pressed={view() === "group"}
          title="Vue agrégée — top N par dimension"
        >
          Groupé
        </button>
      </div>
      <Show when={view() === "group"}>
        <select
          class="panel-select"
          value={groupBy()}
          onChange={(e) => {
            setGroupBy(e.currentTarget.value as ReturnType<typeof groupBy>);
            setPageTick((n) => n + 1);
          }}
          title="Dimension d'agrégation"
        >
          <option value="eventId">Par Event ID</option>
          <option value="provider">Par Source</option>
          <option value="channel">Par Canal</option>
          <option value="level">Par Niveau</option>
          <option value="hour">Par Heure</option>
          <option value="day">Par Jour</option>
        </select>
      </Show>
      <button
        class="btn btn--xs btn--ghost"
        onClick={() => {
          if (view() === "list") {
            resetPagination();
            listQuery.refetch();
          } else {
            groupByQuery.refetch();
          }
        }}
        title="Recharger (sert le cache si frais)"
      >
        ↺
      </button>
      <button
        class="btn btn--xs btn--ghost"
        onClick={forceRefresh}
        title="Forcer un re-scan complet — ignore le cache SQLite côté plugin"
      >
        ↻ Cache
      </button>

      {/* Live updates : abonnement push WS. Le plugin pousse les events
          incrémentaux qui matchent le filtre courant, intervalle configurable. */}
      <div class="event-live-switch" role="group" aria-label="Mode live (push WS)">
        <span class="event-live-switch__label">Live</span>
        <select
          class="panel-select"
          value={liveIntervalId()}
          onChange={(e) => changeLiveInterval(e.currentTarget.value)}
          title="Abonnement push : le plugin pousse les nouveaux events matchant le filtre toutes les N s"
        >
          <For each={LIVE_INTERVALS}>{(p) => <option value={p.id}>{p.label}</option>}</For>
        </select>
        <Show when={liveJobId() != null}>
          <span class="event-live-dot pulse-dot" title="Abonnement actif" aria-hidden="true" />
          <Show when={liveLastPushAt()}>
            <span
              class="event-live-last"
              title={
                liveLastHistoricalCount() > 0
                  ? `Dernier rattrapage : ${liveLastHistoricalCount()} events depuis le cache SQL (changement de filtre)`
                  : `Dernier push : ${new Date(liveLastPushAt()!).toLocaleTimeString()}`
              }
            >
              {formatRelativeTime(liveLastPushAt()!)}
            </span>
          </Show>
        </Show>
      </div>

      <RefreshIndicator state={refresh} />
      <div class="event-filter-levels" role="group" aria-label="Filtrer par niveau">
        <For each={[1, 2, 3, 4, 5]}>
          {(lvl) => (
            <button
              type="button"
              class="btn btn--xs"
              classList={{ "btn--info": levelFilter().has(lvl), "btn--ghost": !levelFilter().has(lvl) }}
              onClick={() => toggleLevel(lvl)}
              title={levelLabel(lvl, "")}
              aria-pressed={levelFilter().has(lvl)}
            >
              {levelLabel(lvl, "").slice(0, 4)}
            </button>
          )}
        </For>
      </div>
      <div class="event-filter-time" role="group" aria-label="Fenêtre temporelle">
        <For each={TIME_PRESETS}>
          {(p) => (
            <button
              type="button"
              class="btn btn--xs"
              classList={{ "btn--info": timePresetId() === p.id, "btn--ghost": timePresetId() !== p.id }}
              onClick={() => changeTimePreset(p.id)}
              aria-pressed={timePresetId() === p.id}
            >
              {p.label}
            </button>
          )}
        </For>
      </div>
      <select
        class="panel-select"
        value={limit()}
        onChange={(e) => changeLimit(Number(e.currentTarget.value))}
        title="Nombre max par page"
      >
        <option value={50}>50</option>
        <option value={100}>100</option>
        <option value={250}>250</option>
        <option value={500}>500</option>
      </select>
    </>
  );

  return (
    <PanelLayout title="Journaux Windows (Event Viewer)" toolbar={toolbar} status={panel.status()}>
      <Show
        when={enabled()}
        fallback={
          <PanelPreview
            feature="eventviewer"
            goal="Consulter, filtrer et fusionner plusieurs journaux Windows pour analyse temporelle. API native EventLogReader, pagination cursor-based, filtres composables."
            tools={[
              { name: "events_channels", description: "Liste des canaux + état (enabled, type, recordCount)" },
              {
                name: "events_list",
                description:
                  "Multi-canal merge (channels[]), filtres composables (levels, providers, excludeProviders, eventIds, messageContains, sinceMs/untilMs), cursor de pagination",
              },
              { name: "events_get", description: "XML complet + description par recordId" },
              { name: "events_search", description: "Recherche substring case-insensitive" },
              { name: "events_clear", description: "Vider un canal (DESTRUCTIF — relais bloque sans userConfirmedAt)" },
            ]}
            notes="Plugin RemoteWS.Plugin.EventViewer livré. Pour activer : host:command plugin-install pluginId=eventviewer."
          />
        }
      >
        <ChannelTree
          rows={normalizedChannelsData()?.rows ?? []}
          selected={activeChannels()}
          isLoading={channelsQuery.isLoading}
          error={channelsQuery.error}
          onChange={(next) => {
            resetPagination();
            setActiveChannels(next);
          }}
          onRefresh={() => channelsQuery.refetch()}
        />

        <details
          class="idle-view-advanced"
          open={advancedOpen()}
          onToggle={(e) => setAdvancedOpen((e.currentTarget as HTMLDetailsElement).open)}
        >
          <summary class="idle-view-advanced__summary">
            Filtres avancés (EventID, Provider, exclusions, contenu)
          </summary>
          <div class="event-advanced-grid">
            <label class="event-advanced-field">
              <span>Event IDs (séparés par virgule)</span>
              <input
                class="panel-filter-input"
                type="text"
                placeholder="1000, 1001, 41"
                value={eventIdsRaw()}
                onInput={(e) => setEventIdsRaw(e.currentTarget.value)}
                onBlur={applyAdvanced}
              />
            </label>
            <label class="event-advanced-field">
              <span>Providers / sources</span>
              <input
                class="panel-filter-input"
                type="text"
                placeholder="Application Error, Microsoft-Windows-Kernel-Power"
                value={providersRaw()}
                onInput={(e) => setProvidersRaw(e.currentTarget.value)}
                onBlur={applyAdvanced}
              />
            </label>
            <label class="event-advanced-field">
              <span>Exclure ces providers</span>
              <input
                class="panel-filter-input"
                type="text"
                placeholder="Service Control Manager"
                value={excludeProvidersRaw()}
                onInput={(e) => setExcludeProvidersRaw(e.currentTarget.value)}
                onBlur={applyAdvanced}
              />
            </label>
            <label class="event-advanced-field">
              <span>Contient (substring dans la description)</span>
              <input
                class="panel-filter-input"
                type="search"
                placeholder="kernel-power, dcom, …"
                value={messageContains()}
                onInput={(e) => setMessageContains(e.currentTarget.value)}
                onBlur={applyAdvanced}
              />
            </label>
            <button class="btn btn--xs btn--primary" type="button" onClick={applyAdvanced}>
              Appliquer
            </button>
          </div>
        </details>

        <div class="panel-split">
          <div class="panel-split-main">
            <Show when={view() === "list"}>
              <Show
                when={!listQuery.isLoading || visibleRows().length > 0}
                fallback={<Skeleton variant="table" rows={10} />}
              >
                <DataTable<EventRow>
                  rows={visibleRows()}
                  columns={columns}
                  onRowActivate={(r) => setSelected(r)}
                  emptyMessage={`Aucun event sur ${[...activeChannels()].join(", ") || "—"} pour les filtres sélectionnés.`}
                />
                <div class="event-pagination">
                  <span class="event-pagination__meta">
                    {visibleRows().length} affichés
                    <Show when={normalizedListData()?.scanned}> · {normalizedListData()!.scanned} scannés</Show>
                    <Show when={normalizedListData()?.truncated}> · résultats tronqués</Show>
                    <Show when={normalizedListData()?.cached}>
                      <span
                        class="event-cache-tag"
                        classList={{ "event-cache-tag--incremental": !!normalizedListData()?.incremental }}
                        title={
                          normalizedListData()?.incremental
                            ? `Cache SQLite + ${normalizedListData()?.incrementalAdded ?? 0} nouveau(x) event(s) ajouté(s) incrémentalement`
                            : "Cache SQLite (TTL 60 s) — page servie sans re-scan"
                        }
                      >
                        cache
                        <Show
                          when={normalizedListData()?.incremental && (normalizedListData()?.incrementalAdded ?? 0) > 0}
                        >
                          {" "}
                          +{normalizedListData()!.incrementalAdded}
                        </Show>
                      </span>
                    </Show>
                  </span>
                  <Show when={normalizedListData()?.nextCursor}>
                    <button
                      type="button"
                      class="btn btn--xs btn--primary"
                      onClick={loadMore}
                      disabled={listQuery.isFetching}
                    >
                      {listQuery.isFetching ? "Chargement…" : "Charger plus ↓"}
                    </button>
                  </Show>
                </div>
              </Show>
            </Show>

            <Show when={view() === "group"}>
              <Show
                when={
                  !groupByQuery.isLoading || ((normalizedGroupData() as GroupResult | undefined)?.rows?.length ?? 0) > 0
                }
                fallback={<Skeleton variant="table" rows={10} />}
              >
                <table class="event-group-table">
                  <thead>
                    <tr>
                      <th>Clé ({groupBy()})</th>
                      <th class="num">Count</th>
                      <th>Dernier vu</th>
                      <th>Sample</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    <For each={normalizedGroupData()?.rows ?? []}>
                      {(g) => (
                        <tr>
                          <td class="event-group-key">{g.key}</td>
                          <td class="num">
                            <strong>{g.count}</strong>
                          </td>
                          <td>
                            <span title={new Date(g.lastSeenMs).toLocaleString()}>
                              {formatRelativeTime(g.lastSeenMs)}
                            </span>
                          </td>
                          <td>
                            <PanelBadge intent={levelIntent(g.sample.level)}>
                              {levelLabel(g.sample.level, "")}
                            </PanelBadge>{" "}
                            <span class="event-group-sample">
                              {g.sample.provider} · evt {g.sample.eventId}
                            </span>
                          </td>
                          <td>
                            <button
                              class="btn btn--xs btn--ghost"
                              onClick={() => drillDown(g)}
                              title="Voir les events de ce groupe en vue Liste"
                            >
                              Drill ↓
                            </button>
                          </td>
                        </tr>
                      )}
                    </For>
                  </tbody>
                </table>
                <div class="event-pagination">
                  <span class="event-pagination__meta">
                    <Show when={normalizedGroupData()}>
                      {normalizedGroupData()!.rows.length} groupes · {normalizedGroupData()!.total} events agrégés ·{" "}
                      {normalizedGroupData()!.scanned} scannés
                    </Show>
                    <Show when={normalizedGroupData()?.cached}>
                      <span class="event-cache-tag" title="Servi depuis le cache du plugin (TTL 30 s)">
                        cache
                      </span>
                    </Show>
                  </span>
                </div>
              </Show>
            </Show>
          </div>
          <Show when={selected()}>
            {(row) => (
              <PanelDetail
                title={`Event ${row().eventId} — ${row().provider}`}
                onClose={() => setSelected(null)}
                sections={
                  normalizedDetailData()
                    ? detailSections(normalizedDetailData()!)
                    : [{ label: "Chargement", children: <Skeleton variant="block" /> }]
                }
              />
            )}
          </Show>
        </div>
      </Show>
    </PanelLayout>
  );
};

registerPanel("eventviewer", EventViewerPanel);
export default EventViewerPanel;
